import { DocumentRecordsListSortBy, DocumentRecordsSortDirection } from '@/__generated__/graphql';
import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { UPDATES_TAB_LIST_DOCUMENT_RECORDS_IDS_QUERY } from '@/components/investors-page/investorDetailsPage/tabs/updatesTab/queries';
import { Loader } from '@/components/loaders/loader';
import {
  createDisclosureSlideMaster,
  createSlideMaster,
  createTableSlide,
  createTitleSlide,
  getPptxImageSize
} from '@/components/managerUpdates-page/tabs/publishedUpdatesTab/publishedUpdates-PPTXTemplate';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { getManagerUpdateName } from '@/components/managerUpdates-page/utils';
import { getSettingsItemValue } from '@/components/settings-page';
import { FirmInfoType } from '@/components/settings-page/platformSettings/firmInfo';
import { ITemplateStylingSettings } from '@/components/settings-page/platformSettings/templatesSettings/types';
import { SYSTEM_SETTINGS_QUERY, TENANT_NAME_QUERY } from '@/components/settings-page/queries';
import { STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY } from '@/components/strategyDetails-page/tabs/updatesTab/queries';
import { TableColumns } from '@/components/table/types';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { IDocumentRecordTemplate } from '@/shared/types/templates/documentRecordTemplate';
import { convertDocumentRecordFieldToFieldEditor } from '@/shared/ui/fieldList';
import { formattingDocumentRecordDataForDocx } from '@/shared/ui/templates/docx-templates/documentRecord-DOCXTemplate';
import { generateDocxTemplate } from '@/shared/ui/templates/docx-templates/utils';
import { DocumentRecordPDFTemplate } from '@/shared/ui/templates/pdf-templates/DocumentRecord-PDFTemplate';
import { UpdatesSummaryPDFTemplate } from '@/shared/ui/templates/pdf-templates/UpdatesSummary-PDFTemplate';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { DocumentRecordStatus } from '@trace-8/trace8-schema';
import { format } from 'date-fns';
import PptxGenJS from 'pptxgenjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

enum RadioButtonOptionEnum {
  UpdatesSummaryTable = 'Updates Summary Table',
  DetailedUpdates = 'Detailed Updates'
}

enum DownloadFormatEnum {
  PPTX = 'PPT',
  PDF = 'PDF',
  DOCX = 'DOCX'
}
const downloadFormatOptions = {
  [RadioButtonOptionEnum.UpdatesSummaryTable]: [createSimpleSelectOption(DownloadFormatEnum.PDF), createSimpleSelectOption(DownloadFormatEnum.PPTX)],
  [RadioButtonOptionEnum.DetailedUpdates]: [createSimpleSelectOption(DownloadFormatEnum.PDF), createSimpleSelectOption(DownloadFormatEnum.DOCX)]
};

const radioButtonOptions = Object.values(RadioButtonOptionEnum).map((option) => option);

interface PublishedUpdatesTabDownloadProps {
  isOpen: boolean;
  publishedUpdatesTableColumns: TableColumns[];
  publishedUpdates: PublishedInvestmentManagerUpdatesDto[];
  fileName: string;
  source: PublishedInvestmentManagerUpdatesSourceEnum;
  onClose: () => void;
}

const PublishedUpdatesTabDownload = ({
  isOpen,
  publishedUpdatesTableColumns,
  publishedUpdates,
  fileName,
  source,
  onClose
}: PublishedUpdatesTabDownloadProps) => {
  const params = useParams();
  const { user } = useAuth();
  const theme = useTheme();
  const { t } = useTranslation('enums');
  const { default: defaultErrorMessage } = useErrorMessages();

  const [selectedRadioButtonOption, setSelectedRadioButtonOption] = useState<RadioButtonOptionEnum>(RadioButtonOptionEnum.UpdatesSummaryTable);
  const [selectedFormat, setSelectedFormat] = useState<DownloadFormatEnum | null>(null);
  const [isDocumentGenerating, setIsDocumentGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [firmInfoData, setFirmInfoData] = useState<FirmInfoType>({} as FirmInfoType);
  const [templateStylingData, setTemplateStylingData] = useState<ITemplateStylingSettings>({});
  const [templateData, setTemplateData] = useState<IDocumentRecordTemplate[]>([]);
  const [templateDataLoading, setTemplateDataLoading] = useState(false);

  useEffect(() => {
    const fetchDocumentRecords = async () => {
      try {
        setTemplateDataLoading(true);

        const templateDataLoadPromises = publishedUpdates.map(async (strategy) => {
          const { data: listDocumentRecordsIdsData } = await client.query({
            query: UPDATES_TAB_LIST_DOCUMENT_RECORDS_IDS_QUERY,
            variables: {
              data: {
                investmentId: strategy?.investment?.id,
                sortBy: DocumentRecordsListSortBy.StatusUpdatedDate,
                sortDirection: DocumentRecordsSortDirection.Desc,
                documentTypeFilter: ['mu'],
                limit: 1000
              }
            }
          });

          const { records } = listDocumentRecordsIdsData?.listDocumentRecords || {};

          const lastPublishedRecord = records.find((record) => record.status === DocumentRecordStatus.Published);
          const lastDraftRecord = records.find((record) => record.status === DocumentRecordStatus.Draft);
          const lastAvailableRecord = lastPublishedRecord ?? lastDraftRecord;

          const { data: documentRecordData } = await client.query({
            query: STRATEGY_DETAILS_PAGE_GET_DOCUMENT_RECORD_QUERY,
            variables: {
              id: lastAvailableRecord?.id || ''
            }
          });

          const { name, fields } = documentRecordData?.getDocumentRecord || {};
          const filteredManagerUpdateFields = fields?.filter(
            (field) => field.documentField.name !== 'Document Record Name' && field.documentField.name !== 'Document Record Publish Date'
          );
          const convertedUpdateTabFields = convertDocumentRecordFieldToFieldEditor(filteredManagerUpdateFields || []);
          const templateDataItem: IDocumentRecordTemplate = {
            updateName: getManagerUpdateName(fields, name),
            investmentName: strategy?.investment?.name ?? '',
            investmentDescription: strategy?.investment?.description ?? '',
            fields: convertedUpdateTabFields
          };
          return templateDataItem;
        });

        const templateDataItems = await Promise.all(templateDataLoadPromises);
        setTemplateData(templateDataItems || []);
      } catch (error) {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      } finally {
        setTemplateDataLoading(false);
      }
    };

    fetchDocumentRecords();
  }, [publishedUpdates]);

  const { data: tenantNameData, loading: tenantNameLoading } = useQuery(TENANT_NAME_QUERY, {
    variables: {
      id: user?.tenant?.id
    }
  });

  const { data: firmInfoQueryData, loading: firmInfoLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        key: 'firmInfo',
        type: 'config',
        allowNullTenantSettings: true
      }
    }
  });

  useEffect(() => {
    if (tenantNameLoading || !tenantNameData || firmInfoLoading || !firmInfoQueryData) return;

    const firmInfo = getSettingsItemValue(firmInfoQueryData?.systemSettings ?? [], 'firmInfo') || {};
    setFirmInfoData({ ...firmInfo, firmName: tenantNameData?.tenant?.name || '' });
  }, [tenantNameLoading, tenantNameData, firmInfoLoading, firmInfoQueryData]);

  const { loading: templateStylingLoading } = useQuery(SYSTEM_SETTINGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      data: {
        tenantId: user?.tenant?.id,
        type: 'customization'
      }
    },
    onCompleted: (data) => {
      const templateStyling = getSettingsItemValue(data?.systemSettings ?? [], 'templateStyling') || {};
      setTemplateStylingData(templateStyling);
    }
  });

  const handleDownloadUpdatesSummaryTablePptx = async () => {
    try {
      setIsDocumentGenerating(true);

      const slideTitle = source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB ? fileName : firmInfoData?.firmName;
      const logoSize = await getPptxImageSize({ url: templateStylingData?.logo, maxWidth: 200, maxHeight: 200 });
      const footerImageSize = await getPptxImageSize({ url: templateStylingData?.logo, maxWidth: 200, maxHeight: 30 });
      const currentDate = format(new Date(), 'MMMM yyyy');
      const pptx = new PptxGenJS();

      createSlideMaster(pptx, currentDate, footerImageSize, firmInfoData, templateStylingData, theme);
      createDisclosureSlideMaster(pptx, currentDate, footerImageSize, firmInfoData, templateStylingData, theme);

      createTitleSlide(pptx, currentDate, slideTitle, logoSize, firmInfoData, templateStylingData, theme);
      createTableSlide(pptx, publishedUpdatesTableColumns, publishedUpdates, templateStylingData, theme, source, t);
      // createDisclosurePreviewSlide(pptx, logoSize, templateStylingData, theme);
      // createDisclosureSlide(pptx, theme);

      const pptxFileName =
        source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB ? fileName : `${firmInfoData?.firmName} Private Capital Summary`;
      await pptx.writeFile({ fileName: `${pptxFileName}.pptx` });
    } catch (error) {
      console.error(error);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGenerateUpdatesSummaryTablePdf = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const slideTitle = source === PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB ? fileName : firmInfoData?.firmName;
      const { pdf } = await import('@react-pdf/renderer');
      const pdfBlob = await pdf(
        <UpdatesSummaryPDFTemplate
          publishedUpdatesTableColumns={publishedUpdatesTableColumns}
          publishedUpdates={publishedUpdates}
          slideTitle={slideTitle}
          firmInfoData={firmInfoData}
          templateStylingData={templateStylingData}
          theme={theme}
          source={source}
          t={t}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGenerateDetailedUpdatesPdf = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      const { pdf } = await import('@react-pdf/renderer');
      const pdfBlob = await pdf(
        <DocumentRecordPDFTemplate
          templateData={templateData}
          firmInfoData={firmInfoData}
          templateStylingData={templateStylingData}
          includeAiSuggested={true}
          theme={theme}
        />
      ).toBlob();

      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleGenerateDetailedUpdatesDocx = async () => {
    try {
      setIsDocumentGenerating(true);
      setErrorMessage(null);

      if (!templateData.length) {
        setErrorMessage(defaultErrorMessage);
        return;
      }

      const formattedData = await formattingDocumentRecordDataForDocx(templateData, templateStylingData, true, theme);
      const docxFileName = `${fileName}.docx`;
      await generateDocxTemplate(formattedData, firmInfoData, templateStylingData, docxFileName, theme);
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setIsDocumentGenerating(false);
    }
  };

  const handleDownload = async (selectedFormat: DownloadFormatEnum) => {
    const downloadUpdatesSummaryTableHandlers = {
      [DownloadFormatEnum.PPTX]: handleDownloadUpdatesSummaryTablePptx,
      [DownloadFormatEnum.PDF]: handleGenerateUpdatesSummaryTablePdf
    };
    const downloadDetailedUpdatesHandlers = {
      [DownloadFormatEnum.PDF]: handleGenerateDetailedUpdatesPdf,
      [DownloadFormatEnum.DOCX]: handleGenerateDetailedUpdatesDocx
    };

    const downloadHandlers =
      selectedRadioButtonOption === RadioButtonOptionEnum.UpdatesSummaryTable ? downloadUpdatesSummaryTableHandlers : downloadDetailedUpdatesHandlers;
    const handler = downloadHandlers[selectedFormat as keyof typeof downloadHandlers];
    if (handler) {
      await handler();
      onClose();
    }
  };

  return (
    <ModalWrappedContent isOpen={isOpen} onClose={onClose} title="Download" showCancelButton={false}>
      {tenantNameLoading || firmInfoLoading || templateStylingLoading || templateDataLoading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <Container>
            <div>
              {radioButtonOptions.map((option) => (
                <RadioButton
                  key={option}
                  name={option}
                  radioButtonText={option}
                  selectedOption={selectedRadioButtonOption}
                  handleRadioChange={() => {
                    setSelectedFormat(null);
                    setSelectedRadioButtonOption(option);
                  }}
                />
              ))}
            </div>
            <div>
              <Select
                label="Select Format"
                value={selectedFormat}
                setValue={(format) => setSelectedFormat(format as DownloadFormatEnum)}
                options={downloadFormatOptions[selectedRadioButtonOption]}
                size="lg"
                isRequiredField
              />
            </div>
          </Container>
          <DownloadButtonWrappper>
            <Button
              onClick={() => handleDownload(selectedFormat)}
              styleType={!selectedFormat || isDocumentGenerating ? 'disabled' : 'default'}
              isLoading={isDocumentGenerating}
              size="md"
            >
              Download
            </Button>
          </DownloadButtonWrappper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default PublishedUpdatesTabDownload;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DownloadButtonWrappper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 30px;
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 20px;
`;
